import {
  ColDef,
  ColGroupDef,
  ValueGetterParams,
} from '@ag-grid-community/core';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Document } from '@models';
import { LOCATION } from '@ng-web-apis/common';
import {
  EMPTY_CELL,
  EMPTY_ROW,
  ExcelDownloadService,
  FtCellObject,
  getCellObject,
  getColWidths,
} from '@products/services/excel-download.service';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ConfigurationId,
  FundNameData,
  PPSSState,
  ProductData,
  ToggleProps,
  TogglePropsOption,
} from '@types';
import { Logger } from '@utils/logger';
import camelCase from 'lodash/camelCase';
import kebabCase from 'lodash/kebabCase';
import upperFirst from 'lodash/upperFirst';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExcelDataType, WorkBook, WorkSheet, XLSX$Utils } from 'xlsx';
import {
  AgGridColFactoryService,
  PPSSTab,
  PPSSTabName,
} from './ag-grid-col-factory.service';
import { AgGridHelperService } from './ag-grid-helper.service';

const logger = Logger.getLogger('PpssDownloadService');

interface SheetConfig {
  productType: ConfigurationId;
  tabName: PPSSTabName;
  sheetName: string;
  groupDefs: ColGroupDef[];
}

@Injectable({
  providedIn: 'root',
})
export class PpssDownloadService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private agGridColFactoryService: AgGridColFactoryService,
    private agGridHelperService: AgGridHelperService,
    private excelService: ExcelDownloadService,
    private translateService: TranslateService,
    @Inject(LOCATION) readonly locationRef: Location
  ) {}

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Download unfiltered fund list to XLS
   */
  public downloadAsExcel = (
    state: PPSSState,
    productType: ConfigurationId
  ): void => {
    this.agGridHelperService.configurationName = productType;

    // Step 1: Create target filename from product type
    const filename: string = this.getFileName(state, productType);

    // Step 2: Get Unfiltered row data. Same data used for all sheets
    const rowData: ProductData[] = this.getRowData(state);

    // Step 3. Proximal keys
    const proximalKeyTop = `PpssXls${upperFirst(camelCase(productType))}Top`;
    const proximalKeyBottom = `PpssXls${upperFirst(
      camelCase(productType)
    )}Bottom`;
    logger.info('xls proximal keys', proximalKeyTop, proximalKeyBottom);

    // Load Caveats
    forkJoin([
      this.excelService.getProximalCaveat$(proximalKeyTop),
      this.excelService.getProximalCaveat$(proximalKeyBottom),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([topCaveat, bottomCaveat]) => {
        // Import SheetJS
        import('xlsx').then((xlsx) => {
          const xlsxUtils: XLSX$Utils = xlsx.utils;
          // Create new work book
          const workbook: WorkBook = xlsx.utils.book_new();

          // get colDefs for each sheet
          const sheets: SheetConfig[] = this.getSheetConfigs(
            state,
            productType
          );
          logger.debug('sheets', sheets);

          // loop through each sheet, assembling it and adding to workbook
          sheets.forEach((sheet: SheetConfig): void => {
            // assemble sheet
            const worksheet: WorkSheet = this.assembleWorkSheet(
              xlsxUtils,
              sheet.groupDefs,
              rowData,
              topCaveat,
              bottomCaveat,
              state.data.asOfDates.navAsOfDate
            );

            // add worksheet to workbook
            xlsxUtils.book_append_sheet(workbook, worksheet, sheet.sheetName);
          });

          // write workbook
          xlsx.writeFile(workbook, filename);
        });
      });
  };

  /**
   * generate filename for download
   */
  private getFileName = (
    state: PPSSState,
    productType: ConfigurationId
  ): string => {
    const site = state.siteConfig.country.toLocaleLowerCase();
    const productName = kebabCase(productType.toLocaleLowerCase());
    // TODO: check if is nav date is correct for as-of date + formatting
    const asOfDate = kebabCase(state.data.asOfDates.navAsOfDate);
    return `franklin-templeton_${site}_${productName}_${asOfDate}.xlsx`;
  };

  // get PPSS tabs for site+product type combo
  private getSheetConfigs = (
    state: PPSSState,
    productType: ConfigurationId
  ): SheetConfig[] => {
    logger.debug('tabs', state.config.tabs);
    const sheets: SheetConfig[] = [];

    if (state.config.tabs.showPerformance) {
      sheets.push({
        productType,
        tabName: PPSSTabName.PERFORMANCE,
        sheetName: this.translateService.instant('products.performance-tab'),
        groupDefs: this.getColGroupDefs(
          state,
          productType,
          PPSSTabName.PERFORMANCE
        ),
      });
    }

    if (state.config.tabs.showFundIdentifiers) {
      sheets.push({
        productType,
        tabName: PPSSTabName.IDENTIFIERS,
        sheetName: this.translateService.instant(
          'products.fundIdentifiers-tab'
        ),
        groupDefs: this.getColGroupDefs(
          state,
          productType,
          PPSSTabName.IDENTIFIERS
        ),
      });
    }

    if (state.config.tabs.showYield) {
      const yieldTabName =
        !state.isSiteIntl && productType === ConfigurationId.CEF
          ? 'products.yields-tab-cef'
          : 'products.yields-tab';
      sheets.push({
        productType,
        tabName: PPSSTabName.YIELDS,
        sheetName: this.translateService.instant(yieldTabName),
        groupDefs: this.getColGroupDefs(state, productType, PPSSTabName.YIELDS),
      });
    }

    if (state.config.tabs.showRatings) {
      sheets.push({
        productType,
        tabName: PPSSTabName.RATINGS,
        sheetName: this.translateService.instant('products.ratings-tab'),
        groupDefs: this.getColGroupDefs(
          state,
          productType,
          PPSSTabName.RATINGS
        ),
      });
    }

    if (state.config.tabs.showPrice) {
      sheets.push({
        productType,
        tabName: PPSSTabName.PRICE,
        sheetName: this.translateService.instant('products.price-tab'),
        groupDefs: this.getColGroupDefs(state, productType, PPSSTabName.PRICE),
      });
    }

    return sheets;
  };

  // assemble sheet config and data into data to generate xlsx
  private assembleWorkSheet = (
    xlsxUtils: XLSX$Utils,
    colDefs: ColGroupDef[],
    rowData: ProductData[],
    topCaveat: FtCellObject[][],
    bottomCaveat: FtCellObject[][],
    asOfDate: string
  ): WorkSheet => {
    // get cells for top caveat and as of date
    const top: FtCellObject[][] = this.getTopContent(topCaveat, asOfDate);

    // get cells for row headers
    const headers: FtCellObject[][] = this.getHeaders(colDefs);

    // get cells for data rows
    const rows: FtCellObject[][] = rowData.map(this.mapRow(colDefs));

    const dataCells: FtCellObject[][] = headers.concat(rows);

    // get cells for bottom content
    const bottom: FtCellObject[][] = this.getBottomContent(bottomCaveat);

    // assemble into 2D array of cells and return
    const sheetCells: FtCellObject[][] = top
      .concat(EMPTY_ROW)
      .concat(dataCells)
      .concat(EMPTY_ROW)
      .concat(bottom);

    // logger.debug('sheet cells', sheetCells);

    const workSheet: WorkSheet = xlsxUtils.aoa_to_sheet(sheetCells);

    // calc col widths
    // remove first row when calculating widths, so group headers can span multiple cols
    const dataRows = dataCells.slice(1);
    workSheet['!cols'] = getColWidths(dataRows);

    return workSheet;
  };

  // content for top of worksheet
  private getTopContent = (
    topCaveat: FtCellObject[][],
    asOfDate: string
  ): FtCellObject[][] => {
    logger.debug('top caveats', topCaveat);
    const asOf = `${this.translateService.instant(
      'products.xls_as-of'
    )} ${asOfDate}`;
    return topCaveat.concat([
      [], // empty row
      [getCellObject(asOf, 's')],
    ]);
  };

  // content for bottom of worksheet
  private getBottomContent = (
    bottomCaveat: FtCellObject[][]
  ): FtCellObject[][] => {
    logger.debug('bottom caveats', bottomCaveat);
    // NB: for now, just return the caveat cells passed in
    // there may be more content in future
    return bottomCaveat;
  };

  /**
   * Get Column Defs for worksheet
   */
  private getColGroupDefs = (
    state: PPSSState,
    productType: ConfigurationId,
    tabName: PPSSTabName
  ): ColGroupDef[] => {
    const mockTabComp = ({
      ...state.data.asOfDates, // copy all as-of date values
      calcType: this.agGridHelperService.calcTypeConfiguration[productType],
      isSiteIntl: state.isSiteIntl,
      productType,
    } as unknown) as PPSSTab; // hack to cast type: https://garbagevalue.com/blog/typescript-custom-to-primitive-type-casting
    logger.debug('mockTabComp', mockTabComp);

    // Fetch the column definitions from factory accordingly.
    const columnDefs = this.agGridColFactoryService.generateColDefs(
      tabName,
      mockTabComp,
      state,
      this.agGridHelperService,
      this.translateService
    ) as ColGroupDef[];
    return columnDefs;
  };

  /**
   * Get unformatted row data for each fund
   */
  private getRowData = (state: PPSSState): ProductData[] =>
    state.data.tableRows;

  // maps a Column Definition into a Cell(s)
  private mapHeader = (colDef: ColDef, data: any): FtCellObject[] => {
    //  switch rendering for specific col defs
    switch (colDef.colId) {
      // these have inline cell renderer, so check on colId
      case 'NAVVALUE':
        // override header from ag grid
        return this.getLabelHeader('products.xls_nav-val-abs');
      case 'NAVCHANGEVALUE':
        // split NAV change into separate % and $ cols
        return this.getNavChangeValueHeader();
      case 'NAVYTDRETURN':
        // override header from ag grid
        return this.getLabelHeader('products.xls_nav-ytd');
      // split since inception value and date into separate cols
      case 'AVG_ANNUAL-MONTHLY-INCEPTION':
      case 'AVG_ANNUAL-QUARTERLY-INCEPTION':
        return this.getInceptionHeader(colDef, data);
    }

    // switch on cell renderers
    switch (colDef.cellRenderer) {
      case 'favFundCellRendererComponent':
        return this.getFavFundHeader();
      case 'fundNameCellRendererComponent':
        return this.getFundNameHeader(colDef);
      case 'expenseRatioCellRendererComponent':
        return this.getExpenseRatioHeader();
      default:
        return this.getDefaultHeader(colDef, data);
    }
  };

  private getDefaultHeader(colDef: ColDef, data: any): FtCellObject[] {
    const metaData = {
      ...data,
      colId: colDef.colId,
    };
    if (colDef.headerName) {
      return [
        getCellObject(colDef.headerName, 's', null, null, null, metaData),
      ];
    }
    if (
      colDef.headerValueGetter &&
      typeof colDef.headerValueGetter === 'function'
    ) {
      const val = colDef.headerValueGetter();
      return [getCellObject(val, 's', null, null, null, metaData)];
    }
    return [EMPTY_CELL];
  }

  private getLabelHeader(labelKey: string): FtCellObject[] {
    return [getCellObject(this.translateService.instant(labelKey), 's')];
  }

  private getNavChangeValueHeader(): FtCellObject[] {
    return [
      getCellObject(
        this.translateService.instant('products.xls_nav-change-val-percent'),
        's'
      ),
      getCellObject(
        this.translateService.instant('products.xls_nav-change-val-abs'),
        's'
      ),
    ];
  }

  private getInceptionHeader(colDef: ColDef, data?: any): FtCellObject[] {
    const inceptionHeaders: FtCellObject[] = [
      getCellObject(
        this.translateService.instant('products.xls_since-inception-val'),
        's'
      ),
    ];

    // hacky way to add inception date column without 4 duplicate copies
    // only show it on the last performance combo (i.e. Quarterly POP)
    // TODO: probably need a more robust solution for other product types
    if (
      colDef.colId === 'AVG_ANNUAL-QUARTERLY-INCEPTION' &&
      data.calcType === 'POP'
    ) {
      inceptionHeaders.push(
        getCellObject(
          this.translateService.instant('products.xls_since-inception-date'),
          's'
        )
      );
    }

    return inceptionHeaders;
  }

  private getFavFundHeader(): FtCellObject[] {
    // fav fund column is hidden in xls
    return [];
  }

  private getFundNameHeader(colDef: ColDef): FtCellObject[] {
    const headerCells: FtCellObject[] = [
      getCellObject(colDef.headerName, 's'),
      getCellObject(this.translateService.instant('products.xls_ticker'), 's'),
    ];

    // add in sales charges cells etc
    if (colDef.cellRendererParams.showSalesCharges) {
      headerCells.push(
        getCellObject(
          this.translateService.instant('products.xls_max-charges'),
          's'
        )
      );
      headerCells.push(
        getCellObject(this.translateService.instant('products.xls_cdsc'), 's')
      );
    }

    return headerCells;
  }

  private getExpenseRatioHeader(): FtCellObject[] {
    return [
      getCellObject(
        this.translateService.instant('products.xls_expense-ratio-gross'),
        's'
      ),
      getCellObject(
        this.translateService.instant('products.xls_expense-ratio-net'),
        's'
      ),
    ];
  }

  // maps a Column group Defintion into a 2D array of cells
  private mapHeaderGroup = (groupDef: ColGroupDef): FtCellObject[][] => {
    let headers: FtCellObject[];
    if (groupDef.groupId === 'PERFORMANCE') {
      // map out every performance charges*period combination
      headers = this.mapPerformanceGroupHeaders(groupDef);
    } else {
      headers = groupDef.children.map(this.mapHeader).flat();
    }

    let groupRow: FtCellObject[];

    if (groupDef.headerGroupComponent === 'dateGroupHeaderComponent') {
      groupRow = this.getDateHeaderGroup(groupDef, headers);
    } else if (
      groupDef.headerGroupComponent === 'ratingsGroupHeaderComponent'
    ) {
      groupRow = this.getRatingsHeaderGroup(groupDef, headers);
    } else if (groupDef.groupId === 'PERFORMANCE') {
      groupRow = this.getPerformanceHeaderGroup(groupDef, headers);
    } else {
      groupRow = this.getDefaultHeaderGroup(groupDef, headers);
    }

    return [groupRow, headers];
  };

  /**
   * get headers for performance group
   */
  private mapPerformanceGroupHeaders = (
    performanceGroupDef: ColGroupDef
  ): FtCellObject[] => {
    const calcType: string | ToggleProps =
      performanceGroupDef.headerGroupComponentParams.perfTable.calcType;
    // logger.debug('mapPerformanceGroup', performanceGroupDef, calcType);

    if (typeof calcType === 'string') {
      return performanceGroupDef.children.map(this.mapHeader).flat();
    }

    // if calcType options, loop through each
    const cells = calcType.options.map(
      (option: TogglePropsOption, index: number): FtCellObject[] => {
        calcType.selected = option.value;
        let cols: ColDef[] = performanceGroupDef.children;
        if (index < calcType.options.length - 1) {
          // don't duplicate expense ratio + factsheet cols
          cols = cols.filter(
            (col: ColDef): boolean =>
              !['EXPENSERATIO', 'FUND-DOCUMENT'].includes(col.colId)
          );
        }
        return cols
          .map((col: ColDef): FtCellObject[] => {
            return this.mapHeader(col, { calcType: option.value });
          })
          .flat();
      }
    );
    logger.debug('mapPerformanceGroupHeaders()', cells);

    return cells.flat();
  };

  private getDefaultHeaderGroup(
    groupDef: ColGroupDef,
    headers: FtCellObject[]
  ): FtCellObject[] {
    const groupRow: FtCellObject[] = new Array(headers.length).fill(EMPTY_CELL);
    if (groupDef.headerName) {
      groupRow[0] = getCellObject(groupDef.headerName, 's');
    }

    return groupRow;
  }

  private getDateHeaderGroup(
    groupDef: ColGroupDef,
    headers: FtCellObject[]
  ): FtCellObject[] {
    const groupRow: FtCellObject[] = new Array(headers.length).fill(EMPTY_CELL);

    let asOfDateText = '';
    const asOfDateTextFunc = groupDef.headerGroupComponentParams?.asOfDateText;
    if (asOfDateTextFunc) {
      asOfDateText = `${this.translateService.instant(
        'pds.PERF_AS_OF_DATE'
      )} ${asOfDateTextFunc()}`;
    }
    groupRow[0] = getCellObject(asOfDateText, 's');

    return groupRow;
  }

  private getPerformanceHeaderGroup(
    groupDef: ColGroupDef,
    headers: FtCellObject[]
  ): FtCellObject[] {
    const calcType: string | ToggleProps =
      groupDef.headerGroupComponentParams.perfTable.calcType;

    const groupRow: FtCellObject[] = new Array(headers.length).fill(EMPTY_CELL);

    if (typeof calcType === 'string') {
      // for now return empty group row if single calcType
      return groupRow;
    }

    // loop the headers, setting the corresponding groupRow cell when required
    headers.forEach((cell: FtCellObject, index: number): void => {
      const cellCalcType = cell.data?.calcType;
      const asOf = this.translateService.instant('pds.PERF_AS_OF_DATE');
      const perfViewHeader = cellCalcType
        ? this.translateService.instant(
            `products.perf-view-header-avg-annual-${cellCalcType}`
          )
        : '';
      let sectionTitle = '';
      let duration = '';
      if (cell.data?.colId === 'AVG_ANNUAL-MONTHLY-1YR') {
        duration = this.translateService.instant('products.xls_monthly');
        // tslint:disable-next-line:max-line-length
        sectionTitle = `${perfViewHeader} ${duration} ${asOf} ${groupDef.headerGroupComponentParams.perfTable.performanceAsOfDateMonthly}`;
        groupRow[index] = getCellObject(sectionTitle, 's');
      }
      if (cell.data?.colId === 'AVG_ANNUAL-QUARTERLY-1YR') {
        duration = this.translateService.instant('products.xls_quarterly');
        // tslint:disable-next-line:max-line-length
        sectionTitle = `${perfViewHeader} ${duration} ${asOf} ${groupDef.headerGroupComponentParams.perfTable.performanceAsOfDateQuarterly}`;
        groupRow[index] = getCellObject(sectionTitle, 's');
      }
    });

    return groupRow;
  }

  private getRatingsHeaderGroup(
    groupDef: ColGroupDef,
    headers: FtCellObject[]
  ): FtCellObject[] {
    const groupRow: FtCellObject[] = new Array(headers.length).fill(EMPTY_CELL);

    const asOfDateText = `${this.translateService.instant(
      'products.ratings-title'
    )} ${this.translateService.instant('products.as-of')} ${
      groupDef.headerGroupComponentParams.ratingsAsOfDate
    } ${
      !groupDef.headerGroupComponentParams.isSiteIntl
        ? this.translateService.instant(
            'products.ratings-risk-adjusted-returns'
          )
        : ''
    }`;
    groupRow[0] = getCellObject(asOfDateText, 's');

    return groupRow;
  }

  /**
   * Get header row(s) from col defs
   */
  private getHeaders = (groupDefs: ColGroupDef[]): FtCellObject[][] => {
    const mappedGroups: FtCellObject[][][] = groupDefs.map(this.mapHeaderGroup);
    const groupRow: FtCellObject[] = mappedGroups.map((a) => a[0]).flat();
    const headerRow: FtCellObject[] = mappedGroups.map((a) => a[1]).flat();

    return [groupRow, headerRow];
  };

  /**
   * Get formatted row data for fund
   */
  private mapRow = (colDefs: ColDef[]) => (
    row: ProductData
  ): FtCellObject[] => {
    // logger.debug('mapRow', colDefs, row);
    return colDefs.map(this.mapColOrGroup(row)).flat();
  };

  /**
   * Get cell(s) for combination of colDef and fund row
   * Can be called recursively in case of col group
   */
  private mapColOrGroup = (row: ProductData, calcType?: string) => (
    colOrGroupDef: ColDef | ColGroupDef
  ): FtCellObject[] => {
    if ('groupId' in colOrGroupDef) {
      if (colOrGroupDef.groupId === 'PERFORMANCE') {
        // map out every performance charges*period combination
        return this.mapPerformanceGroup(colOrGroupDef, row);
      }
      // col def is a group, so map children
      return colOrGroupDef.children.map(this.mapColOrGroup(row)).flat();
    }
    // otherwise this is a col, so get cell data
    return this.getCell(colOrGroupDef, row, calcType);
  };

  /**
   * Get  data for performance group cells
   */
  private mapPerformanceGroup = (
    performanceGroupDef: ColGroupDef,
    row: ProductData
  ): FtCellObject[] => {
    const calcType: string | ToggleProps =
      performanceGroupDef.headerGroupComponentParams.perfTable.calcType;
    // logger.debug('mapPerformanceGroup', performanceGroupDef, row, calcType);
    if (typeof calcType === 'string') {
      return performanceGroupDef.children.map(this.mapColOrGroup(row)).flat();
    }

    // if calcType options, loop through each
    return calcType.options
      .map((option: TogglePropsOption, index: number): FtCellObject[] => {
        calcType.selected = option.value;
        let cols: ColDef[] = performanceGroupDef.children;
        if (index < calcType.options.length - 1) {
          // don't duplicate expense ratio + factsheet cols
          cols = cols.filter(
            (col: ColDef): boolean =>
              !['EXPENSERATIO', 'FUND-DOCUMENT'].includes(col.colId)
          );
        }
        return cols.map(this.mapColOrGroup(row, calcType.selected)).flat();
      })
      .flat();
  };

  /**
   * Get cell(s) for combination of colDef and fund row
   * this will delegate to a separate renderer functions
   */
  private getCell = (
    colDef: ColDef,
    row: ProductData,
    calcType?: string
  ): FtCellObject[] => {
    //  switch rendering for specific col defs
    switch (colDef.colId) {
      // these have inline cell renderer, so check on colId
      case 'SHARECLASS-NAME':
        // specify this as a string, so it doesn't try parsing number
        return this.getCellDefault(colDef, row, 's');
      case 'NAVCHANGEVALUE':
        // split NAV change into separate % and $ cols
        return this.getCellNavChangeValue(colDef, row);
      // split since inception value and date into separate cols
      case 'AVG_ANNUAL-MONTHLY-INCEPTION':
      case 'AVG_ANNUAL-QUARTERLY-INCEPTION':
        return this.getCellInception(colDef, row, calcType);
    }

    // switch on cell renderers
    switch (colDef.cellRenderer) {
      case 'favFundCellRendererComponent':
        return this.getCellFavFund(colDef, row);
      case 'fundNameCellRendererComponent':
        return this.getCellFundName(colDef, row);
      case 'expenseRatioCellRendererComponent':
        return this.getCellExpenseRatio(colDef, row);
      case 'fundDocumentCellRendererComponent':
        return this.getCellFundDocument(colDef, row);
      default:
        return this.getCellDefault(colDef, row);
    }
  };

  /**
   * Gets cell value using col def valueGetter
   */
  private getCellValue = (colDef: ColDef, row: ProductData): string => {
    let val = '';
    if (colDef.valueGetter) {
      if (typeof colDef.valueGetter === 'string') {
        val = colDef.valueGetter;
      } else {
        val = colDef.valueGetter({
          data: row,
          context: null, // TODO: define context for performance cols
        } as ValueGetterParams);
      }
    }
    return val;
  };

  /**
   * Get cell(s) for basic col def+row
   */
  private getCellDefault = (
    colDef: ColDef,
    row: ProductData,
    dataType?: ExcelDataType
  ): FtCellObject[] => {
    const val: string = this.getCellValue(colDef, row);
    return [getCellObject(val, dataType)];
  };

  /**
   * Get cell for Favorite Fund
   */
  private getCellFavFund = (
    colDef: ColDef,
    row: ProductData
  ): FtCellObject[] => {
    // fav fund col is hidden in xls for now
    return [];
  };

  /**
   * Get cells for fund name
   */
  private getCellFundName = (
    colDef: ColDef,
    row: ProductData
  ): FtCellObject[] => {
    const fundDetail: FundNameData = this.agGridHelperService.getFundDetailToDisplay(
      row,
      colDef.cellRendererParams.isSiteIntl,
      false
    );

    const cells: FtCellObject[] = [
      getCellObject(
        fundDetail.fundName,
        's',
        this.locationRef.origin + row.shareClass.link,
        this.translateService.instant('products.xls_fund-name-hover-text')
      ),
      getCellObject(fundDetail?.suffix || '', 's'),
    ];

    // add in sales charges cells etc
    if (colDef.cellRendererParams.showSalesCharges) {
      const salesCharge = row.shareClass?.charges?.maxIntialSalesCharge;
      const cdscValue = row.shareClass?.charges?.cdsc;
      cells.push(getCellObject(salesCharge, 'n'));
      cells.push(getCellObject(cdscValue, 'n'));
    }

    return cells;
  };

  /**
   * Get cells for NAV Change
   */
  private getCellNavChangeValue = (
    colDef: ColDef,
    row: ProductData
  ): FtCellObject[] => {
    return [
      getCellObject(row.shareClass?.nav?.navChangePercentage, 'n'),
      getCellObject(row.shareClass?.nav?.navChangeValue, 'n'),
    ];
  };

  /**
   * Get cells for since inception
   */
  private getCellInception = (
    colDef: ColDef,
    row: ProductData,
    calcType?: string
  ): FtCellObject[] => {
    const cells: FtCellObject[] = [
      getCellObject(this.getCellValue(colDef, row)),
    ];

    // hacky way to add inception date column without 4 duplicate copies
    // only show it on the last performance combo (i.e. Quarterly POP)
    // TODO: probably need a more robust solution for other product types
    if (
      colDef.colId === 'AVG_ANNUAL-QUARTERLY-INCEPTION' &&
      calcType === 'POP'
    ) {
      cells.push(
        getCellObject(row.shareClass?.performanceInceptionDateStd, 'd')
      );
    }

    return cells;
  };

  /**
   * Get cells for expense ratio
   */
  private getCellExpenseRatio = (
    colDef: ColDef,
    row: ProductData
  ): FtCellObject[] => {
    return [
      getCellObject(row.shareClass?.charges.expenseRatioGross, 'n'),
      getCellObject(row.shareClass?.charges.expenseRatioNet, 'n'),
    ];
  };

  /**
   * Get cell for Fact Sheet link
   */
  private getCellFundDocument = (
    colDef: ColDef,
    row: ProductData
  ): FtCellObject[] => {
    const documents: Document[] = row.documents;
    const doc = documents?.find((d: Document): boolean => d.showOnPpss);
    return doc
      ? [
          getCellObject(
            this.translateService.instant('products.xls_fund-document'),
            's',
            this.locationRef.origin + doc.link,
            this.translateService.instant(
              'products.xls_fund-document-hover-text'
            )
          ),
        ]
      : [EMPTY_CELL];
  };
}
